import type { Database } from 'sql.js';
import { newPrefixedPublicId } from '@core/domain/secureRandom.ts';
import {
  insertRowId,
  queryOne,
  rowFirstKeyColumn as rowCol,
  sqlIntOrZero,
  sqlPositiveIntId,
  type SqlJsBind,
} from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';

type SqlBind = SqlJsBind;

/** True when the voucher id is already tied to a redemption line (if that table exists). */
export function isBonoAlreadyRedimido(db: Database, bonoId: number): boolean {
  if (!Number.isFinite(bonoId) || bonoId <= 0) return false;
  if (
    queryOne(db, `SELECT 1 FROM sqlite_master WHERE type='table' AND name='redencion_bono_participante'`, []) ==
    null
  ) {
    return false;
  }
  return queryOne(db, `SELECT 1 AS x FROM redencion_bono_participante WHERE bono_id = ? LIMIT 1`, [bonoId]) != null;
}

/**
 * Maps voucher caption to `bonos.id`: numeric id, `nombre`, or `public_id` (case-insensitive).
 * With positive {@link tipoBonoIdHint}, only rows matching that tipo are accepted.
 */
export function resolveBonoFkIdFromUsuarioCaption(
  db: Database,
  raw: string,
  tipoBonoIdHint: number,
): number | null {
  const t = raw.trim();
  if (!t) return null;
  if (queryOne(db, `SELECT 1 FROM sqlite_master WHERE type='table' AND name='bonos'`, []) == null) {
    return null;
  }

  const matchesTipo = Number.isFinite(tipoBonoIdHint) && tipoBonoIdHint > 0;

  if (/^\d+$/.test(t)) {
    const id = Number(t);
    if (!Number.isSafeInteger(id) || id <= 0) return null;
    const row = matchesTipo
      ? queryOne(db, `SELECT id FROM bonos WHERE id = ? AND estado = 1 AND tipo_bono_id = ?`, [id, tipoBonoIdHint])
      : queryOne(db, `SELECT id FROM bonos WHERE id = ? AND estado = 1`, [id]);
    if (!row) return null;
    return sqlPositiveIntId(rowCol(row, 'id', 'ID'));
  }

  let sql = `SELECT id FROM bonos WHERE estado = 1 AND (nombre = ? COLLATE NOCASE OR public_id = ? COLLATE NOCASE)`;
  const binds: SqlBind[] = [t, t];
  if (matchesTipo) {
    sql += ` AND tipo_bono_id = ?`;
    binds.push(tipoBonoIdHint);
  }
  sql += ` LIMIT 1`;
  const row = queryOne(db, sql, binds);
  if (!row) return null;
  return sqlPositiveIntId(rowCol(row, 'id', 'ID'));
}

/**
 * Resolves `bonos.id` like {@link resolveBonoFkIdFromUsuarioCaption}; if nothing matches and
 * {@link tipoBonoId} is a positive, active catalog id, inserts a new `bonos` row using the trimmed
 * caption as `nombre` (user-entered voucher identifier in capture flows).
 *
 * When {@link tipoBonoId} is non-positive, only existing rows are matched (same as
 * {@link resolveBonoFkIdFromUsuarioCaption} without a tipo filter); inserting a new `bonos` row
 * requires a valid tipo because `bonos.tipo_bono_id` is NOT NULL.
 *
 * Returns `null` when the caption is empty, creation is attempted with an invalid tipo, there is no
 * match with a non-positive tipo, or `nombre` clashes across tipos on the reconcile path (unique
 * global `nombre`).
 */
export function ensureBonoIdForPuntoEntregaCaptura(
  db: Database,
  raw: string,
  tipoBonoId: number,
): { bonoId: number; inserted: boolean } | null {
  const existingId = resolveBonoFkIdFromUsuarioCaption(db, raw, tipoBonoId);
  if (existingId != null && existingId > 0) {
    return { bonoId: existingId, inserted: false };
  }

  const t = raw.trim();
  if (!t) return null;

  if (queryOne(db, `SELECT 1 FROM sqlite_master WHERE type='table' AND name='bonos'`, []) == null) {
    return null;
  }

  const tipoOk =
    Number.isFinite(tipoBonoId) &&
    tipoBonoId > 0 &&
    queryOne(db, `SELECT 1 FROM tipos_bonos WHERE id = ? AND estado = 1`, [tipoBonoId]) != null;
  if (!tipoOk) return null;

  try {
    const publicId = newPrefixedPublicId('bn');
    const bonoId = insertRowId(
      db,
      `INSERT INTO bonos (tipo_bono_id, public_id, nombre, estado) VALUES (?, ?, ?, 1)`,
      [tipoBonoId, publicId, t],
    );
    if (!bonoId || bonoId <= 0) return null;
    return { bonoId, inserted: true };
  } catch {
    const row = queryOne(
      db,
      `SELECT id, tipo_bono_id FROM bonos WHERE nombre = ? COLLATE NOCASE AND estado = 1 LIMIT 1`,
      [t],
    );
    if (!row) return null;
    const rowTipo = sqlIntOrZero(rowCol(row, 'tipo_bono_id', 'TIPO_BONO_ID'));
    if (rowTipo !== tipoBonoId) return null;
    const id = sqlPositiveIntId(rowCol(row, 'id', 'ID'));
    if (id == null) return null;
    return { bonoId: id, inserted: false };
  }
}
